import { z } from 'zod';

import { analyticsModeSchema } from './analytics.schema';
import { bookingStatusSchema } from './booking.schema';
import { parametersSchemaDefinition } from './custom-tool.schema';

export const workflowNodeTypeSchema = z.enum([
  'start',
  'condition',
  'send_email',
  'send_sms',
  'update_call',
  'update_contact',
  'enrich_data',
  'run_review',
  'run_classifier',
  'run_quality',
  'agent',
  'call_api',
  'booking',
  'outbound_call',
  'get_analytics',
  'get_quality_issues',
  'stock',
  'create_payment',
  'tracking_link',
  'function',
  'notification',
  'collect',
  'campaign',
  'handoff',
  'return',
  'end',
]);

export type WorkflowNodeType = z.infer<typeof workflowNodeTypeSchema>;

export const conditionOperatorSchema = z.enum([
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal',
  'is_empty',
  'is_not_empty',
  'in_list',
]);

export type ConditionOperator = z.infer<typeof conditionOperatorSchema>;

export const startNodeDataSchema = z.object({
  label: z.string(),
});

export const conditionRuleSchema = z.object({
  field: z.string(),
  operator: conditionOperatorSchema,
  value: z.string(),
});

export type ConditionRule = z.infer<typeof conditionRuleSchema>;

export const conditionNodeDataSchema = z.object({
  label: z.string(),
  // Single rule (backwards compatible)
  field: z.string(),
  operator: conditionOperatorSchema,
  value: z.string(),
  // Compound rules (optional, overrides single rule when present)
  rules: z.array(conditionRuleSchema).optional(),
  logic: z.enum(['AND', 'OR']).optional(),
});

export const sendEmailNodeDataSchema = z.object({
  label: z.string(),
  to: z.string(),
  subject: z.string(),
  body: z.string().nullable(),
  templateId: z.string().nullable(),
  connectorId: z.string().uuid().nullable().optional(),
  fromEmail: z.string().email().nullable().optional(),
});

export const sendSmsNodeDataSchema = z.object({
  label: z.string(),
  to: z.string(),
  body: z.string().nullable(),
  templateId: z.string().nullable(),
  defaultCountryCode: z.string().optional(),
  /**
   * Call to attribute the SMS (and its tracking links) to. Template-resolved.
   * Needed on webhook/scheduled runs, which have no call context — e.g.
   * "{{webhook.body.callId}}". Falls back to the run's call context.
   */
  callId: z.string().optional(),
  provider: z.enum(['twilio', 'telnyx', 'whatsapp']).optional(),
  from: z.string().optional(),
  /**
   * WhatsApp only: what the node sends. 'template' (default) keeps the
   * existing template behaviour; 'contacts' shares a contact card built from
   * `waContactName` + `waContactPhone` instead.
   */
  waMessageType: z.enum(['template', 'contacts']).optional(),
  waContactName: z.string().optional(),
  waContactPhone: z.string().optional(),
  /** Optional overrides for the derived name split (Meta requires first/last
   *  next to formatted_name) and the card's company line. */
  waContactFirstName: z.string().optional(),
  waContactLastName: z.string().optional(),
  waContactCompany: z.string().optional(),
  waTemplateName: z.string().optional(),
  waTemplateLanguage: z.string().optional(),
  waTemplateVariables: z.array(z.string()).optional(),
  waTemplateHeaderVariables: z.array(z.string()).optional(),
  waTemplateButtonVariables: z.array(z.string()).optional(),
  waFromNumberId: z.string().optional(),
  /**
   * Send later instead of now. Template resolving to a date/datetime — e.g.
   * "{{booking.iso}}" — optionally shifted by `offsetMinutes` (negative for
   * "before"). The message is fully resolved when the workflow runs and
   * stored on scheduled_messages, so what the page shows is what goes out.
   * A time that resolves to the past sends immediately.
   */
  sendAt: z.string().optional(),
  offsetMinutes: z.number().int().optional(),
  /**
   * Scheduled sends only: template resolving to a bookings row id. Ties the
   * queued message to that booking, so cancelling or rescheduling the visit
   * drops the reminder instead of letting it go out for a visit that moved.
   */
  bookingId: z.string().optional(),
});

export const updateCallNodeDataSchema = z.object({
  label: z.string(),
  callId: z.string().optional(),
  callSid: z.string().optional(),
  mappings: z.array(
    z.object({
      field: z.string(),
      value: z.string(),
    }),
  ),
});

export const updateContactNodeDataSchema = z.object({
  label: z.string(),
  contactId: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  fetchContactData: z.boolean().optional(),
  mappings: z.array(
    z.object({
      field: z.string(),
      value: z.string(),
    }),
  ),
  dataBody: z.string().nullable().optional(),
});

export const enrichDataNodeDataSchema = z.object({
  label: z.string(),
  action: z.string(),
  input: z.string(),
  outputVariable: z.string(),
});

export const reviewInputMappingSchema = z.object({
  contextKey: z.string(),
  workflowVar: z.string(),
});

export type ReviewInputMapping = z.infer<typeof reviewInputMappingSchema>;

export const runReviewNodeDataSchema = z.object({
  label: z.string(),
  reviewConfigId: z.string(),
  outputKey: z.string().default('review'),
  inputMappings: z.array(reviewInputMappingSchema).default([]),
  /**
   * Messaging channels only. A WhatsApp/web/email "call" is one session of a
   * long-lived thread, and by default the agents widen their view to the
   * surrounding conversation so a mid-thread slice still reads sensibly. Set
   * this when each session should be judged strictly on its own — the agent
   * then sees this session and nothing before it. No effect on voice calls,
   * which have no thread to widen into.
   */
  sessionOnly: z.boolean().optional(),
});

/**
 * Phase 4 — workflow node that runs the ClassifierAgent. Optional configId
 * lets a workflow override the per-DB default; omit to use whichever
 * classifier_configs row has is_default = true.
 */
export const runClassifierNodeDataSchema = z.object({
  label: z.string(),
  classifierConfigId: z.string().optional(),
  outputKey: z.string().default('classification'),
  /** See runReviewNodeDataSchema.sessionOnly. */
  sessionOnly: z.boolean().optional(),
});

/**
 * Phase 4 — workflow node that runs the QualityAgent. Same shape as
 * RunClassifier; QualityAgent reads audio + transcript internally.
 */
export const runQualityNodeDataSchema = z.object({
  label: z.string(),
  qualityConfigId: z.string().optional(),
  outputKey: z.string().default('quality'),
});

/**
 * Where an agent node mirrors its output, in addition to the workflow context.
 *
 * - `none`           — context only. The output is still available to later nodes.
 * - `classification` — upsert into call_classifications under `name = outputKey`.
 * - `quality`        — upsert into call_quality_assessments under `name = outputKey`.
 * - `review`         — create a call_reviews row with the full output stored in
 *   `additional_review_data`. Note: calls_with_review promotes the newest
 *   COMPLETED review per call, so an agent-written row will displace any earlier
 *   run_review row for that call.
 */
export const agentPersistTargetSchema = z.enum(['none', 'classification', 'quality', 'review']);

export type AgentPersistTarget = z.infer<typeof agentPersistTargetSchema>;

/**
 * Phase B — a general-purpose AI node: free-form prompt plus a UI-defined output
 * schema, run as a one-shot ADK LlmAgent with no tools. Generalises
 * run_review / run_classifier / run_quality, whose prompt+schema live in a DB
 * config row; here both are inline on the node.
 *
 * `outputKey` is both the workflow context key and the persisted row's `name`.
 * Keeping it off the canonical 'classification' / 'quality' names is what makes
 * a node shadow-safe: calls_with_review joins those two names only, so any other
 * outputKey is invisible to dashboards while you compare its output.
 */
export const agentNodeDataSchema = z.object({
  label: z.string(),
  prompt: z.string().min(10),
  outputSchema: parametersSchemaDefinition,
  outputKey: z.string().min(1).default('agent'),
  /** Attach the call recording. Requires a call-triggered workflow. */
  includeAudio: z.boolean().default(false),
  /**
   * Send the original (raw) transcript rather than the enhanced one. Mirrors
   * review_input_mode = ORIGINAL_TRANSCRIPT.
   */
  includeOriginalTranscript: z.boolean().default(false),
  includeTranscript: z.boolean().default(true),
  model: z.string().optional(),
  temperature: z.number().min(0).max(2).optional(),
  persistTo: agentPersistTargetSchema.default('none'),
  /**
   * Written to the NOT NULL taxonomy_version / rubric_version column when
   * persistTo is set, so agent-written rows stay filterable by prompt revision.
   */
  version: z.string().default('agent-v1'),
});

export type AgentNodeData = z.infer<typeof agentNodeDataSchema>;

export const callApiNodeDataSchema = z.object({
  label: z.string(),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  url: z.string(),
  headers: z.array(z.object({ key: z.string(), value: z.string() })),
  body: z.string().nullable(),
  outputKey: z.string().default('api_response'),
});

export const bookingNodeDataSchema = z.object({
  label: z.string(),
  action: z.enum(['add', 'cancel', 'set_status']),
  date: z.string().optional(),
  time: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  /**
   * add only: structured payload written to bookings.data at creation.
   * Dotted field paths inside the blob (e.g. "vehicle.registration"),
   * template-resolvable values — same shape as update_contact mappings.
   */
  dataMappings: z
    .array(
      z.object({
        field: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
  /**
   * cancel / set_status only: template resolving to a bookings row id — when
   * it yields a UUID, that exact booking is updated instead of falling back
   * to the contact's phone.
   */
  bookingId: z.string().optional(),
  /**
   * set_status only: the status to write. `cancel` is kept as its own action
   * for the workflows already using it and behaves like set_status=cancelled.
   */
  status: bookingStatusSchema.optional(),
  /**
   * add only: context key receiving the created booking ({ id, date, time,
   * title, status }). Without it nothing downstream can reference the new
   * row — which is what a reschedule needs in order to re-pin the contact's
   * booking_id to the replacement instead of the superseded booking.
   */
  outputKey: z.string().optional(),
});

export type BookingNodeData = z.infer<typeof bookingNodeDataSchema>;

export const stockNodeDataSchema = z.object({
  label: z.string(),
  action: z.enum(['search', 'list']),
  searchBy: z.enum(['registration', 'vin', 'stock_listing_id']).default('registration'),
  searchValue: z.string(),
  outputKey: z.string().default('stock'),
});

export type StockNodeData = z.infer<typeof stockNodeDataSchema>;

/**
 * Creates a trackable NatWest payment link. Every string field is resolved
 * through VariableResolver, so a preceding Stock node or the call's lead_data
 * can feed it. `amount` is a string rather than a number precisely so it can
 * hold a `{{…}}` template; the executor parses it after resolution.
 */
export const createPaymentNodeDataSchema = z.object({
  label: z.string(),
  callId: z.string().optional(),
  callSid: z.string().optional(),
  vehicleName: z.string().min(1),
  vehicleRegistration: z.string().optional(),
  vehicleId: z.string().optional(),
  /** Blank falls back to the RESERVATION_FEE_AMOUNT setting. */
  amount: z.string().optional(),
  phoneNumber: z.string().optional(),
  /**
   * Payer identity. Sent to NatWest as the billing person/contact so the
   * transaction is attributable in their back office, and stored on the row.
   */
  customerName: z.string().optional(),
  customerEmail: z.string().optional(),
  /** Tick when the workflow goes on to deliver the link (e.g. a Send SMS node). */
  markAsSent: z.boolean().default(false),
  outputKey: z.string().min(1).default('payment'),
});

export type CreatePaymentNodeData = z.infer<typeof createPaymentNodeDataSchema>;

/**
 * Wraps a destination URL in a click-tracking redirect (/r/:id), the same
 * mechanism used for SMS and payment links. Unlike those, this node works
 * without a call — webhook and scheduled runs have none — so `callId` is
 * optional and the tracking row is written with call_id = null.
 */
export const trackingLinkNodeDataSchema = z.object({
  label: z.string(),
  url: z.string().min(1),
  callId: z.string().optional(),
  linkType: z
    .enum([
      'VEHICLE_LINK',
      'RESERVATION_LINK',
      'PART_EXCHANGE_LINK',
      'WORKFLOW',
      'WORKFLOW_VEHICLE_LINK',
      'PAYMENT_LINK',
      'FINANCE_LINK',
    ])
    .default('WORKFLOW_VEHICLE_LINK'),
  outputKey: z.string().min(1).default('tracking'),
});

export type TrackingLinkNodeData = z.infer<typeof trackingLinkNodeDataSchema>;

export const functionNodeDataSchema = z.object({
  label: z.string(),
  code: z.string().min(1),
  input: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
  outputKey: z.string().min(1).default('function_result'),
  timeoutMs: z.number().int().min(100).max(10_000).default(5_000),
});

/**
 * Sends a browser-extension push notification. `title` and `body` are
 * variable-resolvable templates. `to` is an optional comma-separated (or
 * template-resolved) email list — empty means every user with extension
 * notifications enabled.
 */
export const notificationNodeDataSchema = z.object({
  label: z.string(),
  title: z.string().min(1),
  body: z.string().min(1),
  to: z.string().optional(),
});

export type NotificationNodeData = z.infer<typeof notificationNodeDataSchema>;

/**
 * Declarative platform-data query — fills the (otherwise empty) context of
 * scheduled workflows with an array of rows. Entities and filterable fields
 * are whitelisted in the executor; values are template-resolvable. Date
 * fields support the `within` operator with relative tokens (today, tomorrow,
 * yesterday, next_7_days, past_7_days, next_24h, past_24h) or an explicit
 * YYYY-MM-DD day.
 */
export const collectFilterSchema = z.object({
  field: z.string().min(1),
  operator: z.enum([
    'equals',
    'not_equals',
    'contains',
    'in',
    'gt',
    'gte',
    'lt',
    'lte',
    'is_empty',
    'is_not_empty',
    'within',
  ]),
  value: z.string().optional(),
});

export const collectNodeDataSchema = z.object({
  label: z.string(),
  entity: z.enum(['bookings', 'contacts', 'calls', 'payment_links', 'stock_listings']),
  filters: z.array(collectFilterSchema).default([]),
  /** Dotted path used to keep only the first row per value (after ordering). */
  dedupeBy: z.string().optional(),
  /** Whitelisted field to order by; defaults per entity. */
  orderBy: z.string().optional(),
  orderDirection: z.enum(['asc', 'desc']).default('asc'),
  limit: z.number().int().min(1).max(1000).default(200),
  outputKey: z.string().min(1).default('collected'),
});

export type CollectFilter = z.infer<typeof collectFilterSchema>;
export type CollectNodeData = z.infer<typeof collectNodeDataSchema>;

/**
 * Creates (and optionally activates) a campaign from an array in context —
 * the generic sink for collect/function pipelines. Per-contact fields are
 * templates resolved with the row available as {{item...}}; `contactDataKey`
 * is a dotted path INSIDE the item whose object becomes contacts.data
 * verbatim. Name conflicts resolve to already_exists (idempotent daily crons).
 */
export const campaignNodeDataSchema = z.object({
  label: z.string(),
  /** Context path holding the array of items (e.g. the collect node's outputKey). */
  itemsKey: z.string().min(1),
  configurationId: z.string().min(1),
  /** Campaign name template — include a date token to make daily names unique. */
  name: z.string().min(1),
  /** Campaign run date (YYYY-MM-DD, template-resolvable). Defaults to today. */
  runsAt: z.string().optional(),
  channel: z.enum(['PSTN', 'WHATSAPP']).default('PSTN'),
  waTemplateName: z.string().optional(),
  waTemplateLanguage: z.string().optional(),
  /**
   * WHATSAPP only: the whatsapp_phone_numbers row to send from. Needed when the
   * target configuration does not own the number (a number can be the inbound
   * number of only ONE configuration); omit to fall back to that configuration's
   * own inbound WhatsApp number.
   */
  waFromNumberId: z.string().optional(),
  /** WHATSAPP only: ordered body variables as dotted keys on contacts.data. */
  waTemplateBodyFields: z.array(z.string()).optional(),
  waCallOnReply: z.boolean().optional(),
  waCallOnUndelivered: z.boolean().optional(),
  contactName: z.string().default('{{item.name}}'),
  contactPhone: z.string().default('{{item.phone}}'),
  /** Dotted path inside the item for the contact data blob (e.g. "data"). */
  contactDataKey: z.string().optional(),
  activate: z.boolean().default(true),
  outputKey: z.string().min(1).default('campaign'),
});

export type CampaignNodeData = z.infer<typeof campaignNodeDataSchema>;

/**
 * In-chat AI-to-AI handoff (text sessions, WhatsApp first). Meant for webhook
 * workflows backing an agent's custom "hand off" tool: switches the contact's
 * active configuration (sticky routing for subsequent messages), restamps the
 * conversation, and has the TARGET configuration's agent reply to the
 * customer's last message in the same thread. The calling agent should reply
 * with an empty message after the tool returns — the new agent answers.
 */
export const handoffNodeDataSchema = z.object({
  label: z.string(),
  /** Target configuration id (template-resolvable). */
  configurationId: z.string().min(1),
  /** Context for the new agent — why the conversation was handed over. Template-resolvable. */
  summary: z.string().optional(),
  /** How long the contact stays routed to the target configuration. */
  expiresHours: z.number().int().min(1).max(720).default(48),
  /** Call id template to locate the live session; defaults to the webhook body's callId. */
  callId: z.string().optional(),
  outputKey: z.string().min(1).default('handoff'),
});

export type HandoffNodeData = z.infer<typeof handoffNodeDataSchema>;

export const outboundCallNodeDataSchema = z.object({
  label: z.string(),
  configurationId: z.string(),
  to: z.string(),
  fromNumber: z.string().optional(),
  defaultCountryCode: z.string().optional(),
  transport: z.enum(['pstn', 'whatsapp']).optional().default('pstn'),
  delayMinutes: z.number().int().min(0).default(0),
  /**
   * Absolute alternative to delayMinutes — a template resolving to a
   * date/datetime, optionally shifted by `offsetMinutes` (negative for
   * "before"). Takes precedence when it resolves to a valid future time.
   */
  sendAt: z.string().optional(),
  offsetMinutes: z.number().int().optional(),
});

export type OutboundCallNodeData = z.infer<typeof outboundCallNodeDataSchema>;

export const getAnalyticsNodeDataSchema = z.object({
  label: z.string(),
  dateFrom: z.string().default('{{date_offset:0}}'),
  dateTo: z.string().default('{{date_offset:0}}'),
  channel: z.enum(['ALL', 'PSTN', 'WEBRTC', 'WHATSAPP', 'WEB_WIDGET', 'EMAIL']).default('ALL'),
  mode: analyticsModeSchema.default('calls'),
  outputKey: z.string().default('analytics'),
});

export type GetAnalyticsNodeData = z.infer<typeof getAnalyticsNodeDataSchema>;

export const getQualityIssuesNodeDataSchema = z.object({
  label: z.string(),
  dateFrom: z.string().default('{{date_offset:-1}}'),
  dateTo: z.string().default('{{date_offset:-1}}'),
  minSeverity: z.enum(['minor', 'major', 'critical']).default('major'),
  outputKey: z.string().default('quality_issues'),
  callBaseUrl: z.string().optional(),
  detailField: z.enum(['summary', 'evidence', 'reasoning']).default('summary'),
});

export type GetQualityIssuesNodeData = z.infer<typeof getQualityIssuesNodeDataSchema>;

export const workflowNodeDataSchema = z.union([
  startNodeDataSchema,
  conditionNodeDataSchema,
  sendEmailNodeDataSchema,
  sendSmsNodeDataSchema,
  updateCallNodeDataSchema,
  enrichDataNodeDataSchema,
]);

export const workflowNodeSchema = z.object({
  id: z.string(),
  type: workflowNodeTypeSchema,
  position: z.object({ x: z.number(), y: z.number() }),
  data: z.record(z.string(), z.unknown()),
});

export const workflowEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  sourceHandle: z.string().optional(),
});

export const postCallWorkflowSchema = z.object({
  nodes: z.array(workflowNodeSchema),
  edges: z.array(workflowEdgeSchema),
});

export type StartNodeData = z.infer<typeof startNodeDataSchema>;
export type ConditionNodeData = z.infer<typeof conditionNodeDataSchema>;
export type SendEmailNodeData = z.infer<typeof sendEmailNodeDataSchema>;
export type SendSmsNodeData = z.infer<typeof sendSmsNodeDataSchema>;
export type UpdateCallNodeData = z.infer<typeof updateCallNodeDataSchema>;
export type UpdateContactNodeData = z.infer<typeof updateContactNodeDataSchema>;
export type EnrichDataNodeData = z.infer<typeof enrichDataNodeDataSchema>;
export type RunReviewNodeData = z.infer<typeof runReviewNodeDataSchema>;
export type RunClassifierNodeData = z.infer<typeof runClassifierNodeDataSchema>;
export type RunQualityNodeData = z.infer<typeof runQualityNodeDataSchema>;
export type CallApiNodeData = z.infer<typeof callApiNodeDataSchema>;
export type FunctionNodeData = z.infer<typeof functionNodeDataSchema>;
export type WorkflowNode = z.infer<typeof workflowNodeSchema>;
export type WorkflowEdge = z.infer<typeof workflowEdgeSchema>;
export type PostCallWorkflow = z.infer<typeof postCallWorkflowSchema>;

export const workflowTriggerTypeSchema = z.enum([
  'call',
  'dealerkit',
  'any',
  'scheduled',
  'webhook',
  'event',
]);

export type WorkflowTriggerType = z.infer<typeof workflowTriggerTypeSchema>;

// Internal platform events a workflow can subscribe to via trigger_type='event'.
export const workflowTriggerEventSchema = z.enum(['sms.failed']);

export type WorkflowTriggerEvent = z.infer<typeof workflowTriggerEventSchema>;

export const workflowScheduleSchema = z.object({
  cron: z.string(),
  timezone: z.string().default('Europe/London'),
  enabled: z.boolean().default(true),
});

export type WorkflowSchedule = z.infer<typeof workflowScheduleSchema>;

// Workflow entity schemas (the workflow itself, not just the graph)
export const workflowEntitySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  post_call_workflow: postCallWorkflowSchema.nullable().optional(),
  is_default: z.boolean().default(false),
  trigger_type: workflowTriggerTypeSchema.default('call'),
  trigger_event: workflowTriggerEventSchema.nullable().optional(),
  schedule: workflowScheduleSchema.nullable().optional(),
  webhook_token_hash: z.string().nullable().optional(),
  webhook_rate_limit: z.number().int().min(1).max(10000).nullable().optional(),
  webhook_test_payload: z.record(z.string(), z.unknown()).nullable().optional(),
  // Free-form grouping labels for the workflows list. UI-only — the engine
  // never reads them.
  tags: z.array(z.string().min(1).max(64)).max(20).default([]),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type WorkflowEntity = z.infer<typeof workflowEntitySchema>;

export const createWorkflowEntitySchema = workflowEntitySchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export type CreateWorkflowEntityDto = z.infer<typeof createWorkflowEntitySchema>;

export const updateWorkflowEntitySchema = createWorkflowEntitySchema.partial();

export type UpdateWorkflowEntityDto = z.infer<typeof updateWorkflowEntitySchema>;

export const webhookTokenResponseSchema = z.object({
  token: z.string(),
  webhook_url: z.string(),
});

export type WebhookTokenResponse = z.infer<typeof webhookTokenResponseSchema>;

export const workflowTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  type: z.enum(['email', 'sms']),
  subject: z.string().nullable(),
  body: z.string(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type WorkflowTemplate = z.infer<typeof workflowTemplateSchema>;

export const createWorkflowTemplateSchema = workflowTemplateSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export type CreateWorkflowTemplateDto = z.infer<typeof createWorkflowTemplateSchema>;

export const updateWorkflowTemplateSchema = createWorkflowTemplateSchema.partial();

export type UpdateWorkflowTemplateDto = z.infer<typeof updateWorkflowTemplateSchema>;

// ── Workflow Batch Runs ─────────────────────────────────────────────
export const workflowBatchRunStatusSchema = z.enum([
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'STOPPED',
]);

export type WorkflowBatchRunStatus = z.infer<typeof workflowBatchRunStatusSchema>;

export const createWorkflowBatchRunSchema = z.object({
  workflow_id: z.string().uuid(),
  name: z.string().max(255).optional(),
  delay_ms: z.number().int().min(0).default(1000),
  concurrency: z.number().int().min(1).max(50).default(1),
  data: z.array(z.record(z.string(), z.unknown())).min(1),
});

export type CreateWorkflowBatchRunDto = z.infer<typeof createWorkflowBatchRunSchema>;
